import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Smart Indian Currency Compact Formatter (K, L, Cr) for chart axis ticks
const formatShortRupee = (value) => {
  if (value === 0 || value === '0') return '₹0';
  const val = Number(value);
  if (isNaN(val)) return '₹0';
  const absVal = Math.abs(val);
  const sign = val < 0 ? '-' : '';

  if (absVal >= 10000000) {
    // 1 Crore = 1,00,00,000
    const cr = val / 10000000;
    return `${sign}₹${cr % 1 === 0 ? cr : cr.toFixed(2).replace(/\.00$/, '')} Cr`;
  } else if (absVal >= 100000) {
    // 1 Lakh = 1,00,000
    const lk = val / 100000;
    return `${sign}₹${lk % 1 === 0 ? lk : lk.toFixed(1).replace(/\.0$/, '')} L`;
  } else if (absVal >= 1000) {
    // 1 Thousand = 1,000
    const k = val / 1000;
    return `${sign}₹${k % 1 === 0 ? k : k.toFixed(1).replace(/\.0$/, '')} K`;
  }
  return `₹${val.toLocaleString('en-IN')}`;
};

const DistributorReportCharts = ({ charts = {}, loading = false }) => {
  const { trends = [], topDistributors = [], statusBreakdown = [] } = charts;

  if (loading) {
    return (
      <>
        {/* Row 1 Skeleton: Full Width Trend Chart */}
        <div className="row mb-4">
          <div className="col-lg-12 mb-4">
            <div className="panel panel-default with-rounded-corner with-shadow h-100" style={{ marginBottom: 0 }}>
              <div className="panel-heading">
                <h4 className="panel-title">
                  <div className="skeleton-text" style={{ width: '220px', height: '14px' }}></div>
                </h4>
              </div>
              <div className="panel-body p-4 d-flex justify-content-center align-items-center" style={{ height: '280px' }}>
                <div className="skeleton-text" style={{ height: '220px', width: '95%', borderRadius: '6px' }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2 Skeleton: 2 Side-by-Side Charts */}
        <div className="row mb-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={`chart-skeleton-${i}`} className="col-lg-6 mb-4">
              <div className="panel panel-default with-rounded-corner with-shadow h-100" style={{ marginBottom: 0 }}>
                <div className="panel-heading">
                  <h4 className="panel-title">
                    <div className="skeleton-text" style={{ width: '160px', height: '14px' }}></div>
                  </h4>
                </div>
                <div className="panel-body p-4 d-flex justify-content-center align-items-center" style={{ height: '260px' }}>
                  <div
                    className="skeleton-text"
                    style={{ height: '180px', width: i === 1 ? '180px' : '90%', borderRadius: i === 1 ? '50%' : '6px' }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  // 1. Monthly Trends Chart Data (Full Width)
  const trendLabels = trends.map((t) => t.label || t.month);
  const trendBusinessData = trends.map((t) => t.business || 0);
  const trendCommissionData = trends.map((t) => t.commission || 0);

  const trendChartData = {
    labels: trendLabels.length > 0 ? trendLabels : ['No Data'],
    datasets: [
      {
        type: 'bar',
        label: 'Gross Sales (₹)',
        data: trendBusinessData.length > 0 ? trendBusinessData : [0],
        backgroundColor: '#348fe2',
        borderRadius: 4,
        maxBarThickness: 38,
      },
      {
        type: 'line',
        label: 'Commission (₹)',
        data: trendCommissionData.length > 0 ? trendCommissionData : [0],
        borderColor: '#00acac',
        backgroundColor: 'rgba(0, 172, 172, 0.15)',
        tension: 0.3,
        fill: false,
        pointRadius: 5,
        pointBackgroundColor: '#00acac',
        pointHoverRadius: 7,
      },
    ],
  };

  const trendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { boxWidth: 12, font: { size: 12 } } },
      tooltip: {
        callbacks: {
          label: (context) => ` ${context.dataset.label}: ₹${Number(context.parsed.y).toLocaleString('en-IN')}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        grid: { color: '#f1f5f9' },
        ticks: {
          callback: (value) => formatShortRupee(value),
        },
      },
    },
  };

  // 2. Top Distributors Ranking Bar Chart
  const topDistLabels = topDistributors.map((d) => d.name || d.code);
  const topDistSales = topDistributors.map((d) => d.business || 0);

  const topDistChartData = {
    labels: topDistLabels.length > 0 ? topDistLabels : ['No Data'],
    datasets: [
      {
        label: 'Sales Volume (₹)',
        data: topDistSales.length > 0 ? topDistSales : [0],
        backgroundColor: [
          '#6366f1',
          '#3b82f6',
          '#00acac',
          '#f59c1a',
          '#8753de',
        ],
        borderRadius: 4,
        maxBarThickness: 28,
      },
    ],
  };

  const topDistChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => ` Sales: ₹${Number(context.parsed.x).toLocaleString('en-IN')}`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: '#f1f5f9' },
        ticks: { callback: (value) => formatShortRupee(value) },
      },
      y: {
        grid: { display: false },
        ticks: {
          autoSkip: false,
          font: { size: 12 },
        },
      },
    },
  };

  // 3. Status Breakdown Doughnut Data
  const statusLabels = statusBreakdown.map((s) => s.status || 'Unknown');
  const statusAmounts = statusBreakdown.map((s) => s.amount || 0);
  const statusColors = ['#00acac', '#348fe2', '#f59c1a', '#ff5b57', '#8753de'];

  const statusDoughnutData = {
    labels: statusLabels.length > 0 ? statusLabels : ['No Data'],
    datasets: [
      {
        data: statusAmounts.length > 0 ? statusAmounts : [1],
        backgroundColor: statusAmounts.length > 0 ? statusColors.slice(0, statusLabels.length) : ['#e2e8f0'],
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };

  const statusDoughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (context) => ` ${context.label}: ₹${Number(context.parsed).toLocaleString('en-IN')}`,
        },
      },
    },
  };

  return (
    <>
      {/* Row 1: Full-Width Monthly Revenue & Commission Trend Chart */}
      <div className="row mb-4">
        <div className="col-lg-12 mb-4">
          <div className="panel panel-default with-rounded-corner with-shadow h-100" style={{ marginBottom: 0 }}>
            <div className="panel-heading">
              <h4 className="panel-title">Monthly Sales & Commission Trend</h4>
            </div>
            <div className="panel-body p-4">
              <div style={{ height: '280px', position: 'relative' }}>
                <Bar data={trendChartData} options={trendChartOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Top Performing Distributors & Commission Status Breakdown in Next Row */}
      <div className="row mb-4">
        {/* Top Performing Distributors */}
        <div className="col-lg-6 mb-4">
          <div className="panel panel-default with-rounded-corner with-shadow h-100" style={{ marginBottom: 0 }}>
            <div className="panel-heading">
              <h4 className="panel-title">Top Performing Distributors</h4>
            </div>
            <div className="panel-body p-4">
              <div style={{ height: '260px', position: 'relative' }}>
                <Bar data={topDistChartData} options={topDistChartOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Commission Status Breakdown */}
        <div className="col-lg-6 mb-4">
          <div className="panel panel-default with-rounded-corner with-shadow h-100" style={{ marginBottom: 0 }}>
            <div className="panel-heading">
              <h4 className="panel-title">Commission Status Breakdown</h4>
            </div>
            <div className="panel-body p-4">
              <div style={{ height: '260px', position: 'relative' }}>
                <Doughnut data={statusDoughnutData} options={statusDoughnutOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DistributorReportCharts;
